import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "../lib/api";

const CATEGORIES = [
  { key: "leads",        label: "Leads",        route: (r) => `/leads/${r.id}`,        title: (r) => r.name,         sub: (r) => r.company || r.email || "—" },
  { key: "contacts",     label: "Contacts",     route: (r) => `/contacts/${r.id}`,     title: (r) => r.name,         sub: (r) => r.company || r.email || "—" },
  { key: "companies",    label: "Companies",    route: (r) => `/companies/${r.id}`,    title: (r) => r.name,         sub: (r) => r.domain || r.industry || "—" },
  { key: "deals",        label: "Deals",        route: (r) => `/deals/${r.id}`,        title: (r) => r.name,         sub: (r) => r.status ? r.status.replace(/_/g, " ") : "—" },
  { key: "clients",      label: "Clients",      route: (r) => `/clients/${r.id}`,      title: (r) => r.company_name, sub: (r) => `${r.deal_count || 0} deal${r.deal_count === 1 ? "" : "s"}` },
  { key: "moves",        label: "Moves",        route: (r) => `/moves`,                title: (r) => r.title,        sub: (r) => r.due_date ? `due ${r.due_date.slice(0, 10)}` : "—" },
  { key: "deliverables", label: "Deliverables", route: () => `/deliverables`,          title: (r) => r.name,         sub: (r) => r.category || "—" },
];

export default function GlobalSearchPopover({ onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}&limit=5`);
        setResults(data);
      } catch { /* ignore */ }
      setLoading(false);
    }, 220);
    return () => clearTimeout(id);
  }, [q]);

  const total = results
    ? CATEGORIES.reduce((acc, c) => acc + ((results[c.key] || []).length), 0)
    : 0;

  return (
    <div
      className="global-search-pop"
      data-testid="global-search-popover"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="global-search-input-wrap">
        <Search />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search everything…"
          data-testid="global-search-input"
        />
        <button
          type="button"
          aria-label="Close search"
          onClick={onClose}
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--pg-text-mute)" }}
        >
          <X size={16} />
        </button>
      </div>

      <div className="global-search-results">
        {!q.trim() ? (
          <div className="global-search-empty">
            <div className="global-search-empty-title">Search Pragma</div>
            <div className="global-search-empty-sub">Leads, contacts, companies, deals, clients, moves & deliverables.</div>
          </div>
        ) : loading && !results ? (
          <div className="global-search-empty">
            <div className="global-search-empty-sub">Searching…</div>
          </div>
        ) : total === 0 ? (
          <div className="global-search-empty">
            <div className="global-search-empty-title">No matches</div>
            <div className="global-search-empty-sub">Try a different keyword.</div>
          </div>
        ) : (
          CATEGORIES.map((c) => {
            const rows = results?.[c.key] || [];
            if (!rows.length) return null;
            return (
              <div key={c.key} className="global-search-group" data-testid={`global-search-group-${c.key}`}>
                <div className="global-search-group-title">{c.label}</div>
                {rows.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className="global-search-row"
                    data-testid={`global-search-row-${c.key}-${r.id}`}
                    onClick={() => { navigate(c.route(r)); onClose(); }}
                  >
                    <div className="global-search-row-title">{c.title(r) || "—"}</div>
                    <div className="global-search-row-sub">{c.sub(r)}</div>
                  </button>
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
